import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { findReadingUnit, ContentIndexData } from './types/contentIndex';
import { parsePublicationData } from './types/epub';
import { getDefaultRendition } from './utils/epub';
import { UserData } from './userdata/types';
import initServices from './services/initServices';
import * as AppAction from './actions/AppAction';
import * as PageAction from './actions/PageAction';
import { userDataToAppStates } from './reducers/app';
import configureStore from './store/configureStore';
import App from './components/app/App';
import { ComposerBridge } from 'ark-common';

export default abstract class ViewerInstanceBase {
  protected readonly store = configureStore();

  protected abstract readIndex(): Promise<ContentIndexData>;
  protected abstract readPublicationData(location: string): Promise<any>;
  protected abstract resourceDir(location: string): string;
  protected abstract openBookshelf(): void;
  protected abstract openFileDialog(cb: (filePath: string) => void): void;
  protected abstract openFile(filePath: string): void;
  protected abstract readAttachment(id: string): Promise<Blob>;
  protected abstract writeAttachment(id: string, blob: Blob): Promise<void>;
  protected abstract saveWorkspace(main: any, attachments: ReadonlyArray<string>,
                                   fileName: String): Promise<void>;
  protected abstract toggleFullScreen(): void;

  protected abstract composerBridge: ComposerBridge;

  protected reactWillRenderApp() {
    // Does nothing by default.
  }

  private handleOpenBookshelf = () => {
    this.openBookshelf();
  }

  private handleOpenFileDialog = (cb: (filePath: string) => void) => {
    this.openFileDialog(cb);
  }

  private handleOpenFile = (filePath: string) => {
    this.openFile(filePath);
  }

  private handleReadAttachment = (id: string) => {
    return this.readAttachment(id);
  }

  private handleWriteAttachment = (id: string, blob: Blob) => {
    return this.writeAttachment(id, blob);
  }

  private handleSaveWorkspace = (main: any, attachments: ReadonlyArray<string>,
                                 fileName: String) => {
    return this.saveWorkspace(main, attachments, fileName).then(() => {
      this.store.dispatch(AppAction.finishSavingProcess());
    }).catch(() => {
      alert('保存中にエラーが発生しました。');
      this.store.dispatch(AppAction.finishSavingProcess());
    });
  }

  private handleToggleFullScreen = () => {
    this.toggleFullScreen();
  }

  private loadPublication(data: any, location: string, installedComponents: ReadonlyArray<string>) {
    const publication = parsePublicationData(this.contentID, this.resourceDir(location), data);
    this.store.dispatch(AppAction.setPublication(publication, installedComponents));

    let pageIndex = 0;
    const { title, divisions } = getDefaultRendition(publication);

    if (this.componentID !== null && this.componentID in divisions) {
      pageIndex = divisions[this.componentID].firstLinearItem;
    }

    // XXX: 現状「タブ無し」に対応していないため、Appを読み込む前にこれを実行しておく必要がある
    this.store.dispatch(PageAction.openMainTab(title, pageIndex));

    this.reactWillRenderApp();
    ReactDOM.render(
      <Provider store={this.store}>
        <App
          composerBridge={this.composerBridge}
          onOpenBookshelf={this.handleOpenBookshelf}
          onOpenFileDialog={this.handleOpenFileDialog}
          onOpenFile={this.handleOpenFile}
          onReadAttachment={this.handleReadAttachment}
          onWriteAttachment={this.handleWriteAttachment}
          onSaveWorkspace={this.handleSaveWorkspace}
          onToggleFullScreen={this.handleToggleFullScreen}
        />
      </Provider>,
      // XXX : It should be given from implementations.
      document.getElementById('react-root'),
    );

    if (this.workspace !== null) {
      this.store.dispatch(
        AppAction.replaceAppStates(userDataToAppStates(this.workspace, this.store.getState())),
      );
    }
  }

  private async fetchAndLoadPublication() {
    const index = await this.readIndex();

    const { location, components } = findReadingUnit(index, this.contentID);
    if (location === undefined || components === undefined) {
      throw new Error('無効なコンテンツです。');
    }

    this.loadPublication(await this.readPublicationData(location), location, components);
  }

  protected start() {
    // FIXME : Loading should be done in App container.
    this.fetchAndLoadPublication().catch(e => alert(e.message));
  }

  protected constructor(
    private contentID: string,
    private componentID: string | null,
    private workspace: UserData | null,
  ) {
    initServices(this.store);
  }
}
